// What this session's processes did that the turn reading already NAMED.
//
// The ambient reading — 19 processes, 401 file changes — is a fact about a
// window, not about a turn. Even scoped to the agent's own process tree those
// numbers are large in nearly every window of a working session, and nothing in
// them tells a reader which part of the conversation they belong to. What is
// worth a line of the page is a CORRESPONDENCE: a process wrote a file this
// stretch of the conversation edited, or started with a command line that is the
// shell command it ran.
//
// A correspondence is still not causation and this module cannot make it one.
// The session scope establishes WHOSE process it was; it establishes nothing
// about WHICH TURN, because a session spans the whole conversation. Inside that
// scope the join to a turn is time plus a name, so every match carries the BASIS
// it was found on, and callers are expected to render that basis rather than a
// verb that implies the turn caused the event.
//
// Conservative in both channels, deliberately:
//
//   Paths agree only when their last TWO segments agree. A bare basename is the
//   coin-flip case — every repo has a mod.rs, an index.js, a README.md, and a
//   busy window touches hundreds of files — so the floor is the file AND the
//   directory holding it. The related-work anchor gates (lib/relatedWork/
//   gates.js) settled on three segments for a cross-session join over a whole
//   tenant's corpus; two is the right floor here because the corpus is one
//   endpoint over one window of minutes, which is already most of the
//   discrimination that gate was buying.
//
//   Commands agree either VERBATIM — directly, or as the script of a recognized
//   shell wrapper — or on PROGRAM AND FIRST ARGUMENT. A single-token candidate
//   is refused outright:
//   `ls` turning up somewhere in a window is a coincidence, not evidence. Bare
//   containment is refused for the same reason: `git commit -m "git add . later"`
//   contains `git add .` and corroborates nothing.

import { shellToolCommand } from "./shellToolCall.js";
import { commandProper, scriptCommands } from "./shellScript.js";

// The event kinds whose payload can carry a name the turn reading also holds.
// A DnsQuery names a host and a NetworkConnection an address; neither can ever
// correspond to a file path or a shell command, so neither is worth the weight
// of its event_data on the detail leg. They still count in the ambient reading.
export const MATCHABLE_EVENT_TYPES = ["ProcessStart", "FileState", "FileDelete", "FileRename"];

// Builtins run inside the shell and emit no ProcessStart, so a candidate
// leading with one can never match. Dropping them is not a filter for noise —
// it is what stops `cd /repo && cargo test` from being compared as `cd /repo`
// and never reaching the program that actually started.
const SHELL_BUILTINS = new Set([
  "cd", "export", "set", "unset", "source", ".", "alias", "unalias",
  "umask", "true", "false", ":", "pushd", "popd",
]);

// A verbatim containment shorter than this is not evidence of anything — two
// unrelated command lines share `npm i` constantly.
const MIN_VERBATIM_CHARS = 8;
// The path floor: file plus the directory holding it.
const PATH_FLOOR_SEGMENTS = 2;

// Higher wins when the same correspondence is found more than one way, and
// orders which match earns the one line a window gets.
const RANK_EXACT = 3;
const RANK_SUFFIX = 2;
const RANK_FLOOR = 1;

/**
 * A path split into comparable segments: separator-normalized, lowercased,
 * `.` steps dropped.
 *
 * Lowercased because one side is a tool call the agent typed and the other is
 * what the OS reported, and Windows disagrees with itself about case on the
 * same file. Returns [] for anything that is not a path — a URL, an MCP
 * resource id — so those can never anchor a match.
 */
export function pathSegments(path) {
  if (typeof path !== "string") return [];
  const raw = path.trim();
  if (!raw || raw.includes("://")) return [];
  return raw
    .replace(/\\/g, "/")
    .split("/")
    .filter((s) => s && s !== ".")
    .map((s) => s.toLowerCase());
}

/**
 * How many trailing segments two paths agree on. 0 means they do not even
 * share a basename.
 */
export function sharedSuffixDepth(a, b) {
  const A = pathSegments(a);
  const B = pathSegments(b);
  let n = 0;
  while (n < A.length && n < B.length && A[A.length - 1 - n] === B[B.length - 1 - n]) n += 1;
  return n;
}

/**
 * Do these two paths name the same file, as far as this reading is willing to
 * say? `{ rank, basis }` or null.
 *
 * The basis is the whole point of the return value: "the same path" and "the
 * file and its directory" are different amounts of evidence, and a reader who
 * only sees a filename cannot tell which one they were given.
 */
export function matchPath(observed, candidate) {
  const depth = sharedSuffixDepth(observed, candidate);
  if (depth < PATH_FLOOR_SEGMENTS) return null;
  const a = pathSegments(observed);
  const b = pathSegments(candidate);
  if (depth === a.length && depth === b.length) return { rank: RANK_EXACT, basis: "the same path" };
  if (depth > PATH_FLOOR_SEGMENTS) return { rank: RANK_SUFFIX, basis: `the last ${depth} path segments` };
  return { rank: RANK_FLOOR, basis: "the file and the directory holding it" };
}

// Exported because the READING of an event (traceEndpointEvents) has to fold on
// the same string this module matches on — two normalizations would put a
// corresponding row and its own match under different keys.
export function normalizeSpace(s) {
  return String(s || "").replace(/\s+/g, " ").trim();
}

// Whitespace tokens, with the simplest possible quote handling. A command line
// with escaped quotes inside quotes tokenizes wrong here; that costs a match
// that would have been found, never a match that is not there, which is the
// direction this whole module errs in.
function tokenize(s) {
  const out = [];
  const re = /"([^"]*)"|'([^']*)'|(\S+)/g;
  let m = re.exec(s);
  while (m) {
    out.push(m[1] ?? m[2] ?? m[3]);
    m = re.exec(s);
  }
  return out;
}

// A program token reduced to the name a person would recognize: the last path
// segment, without the Windows executable suffix. `/usr/bin/cargo`,
// `C:\bin\cargo.exe` and `cargo` are the same program and must compare equal.
export function programName(token) {
  const segs = pathSegments(token);
  const last = segs.length ? segs[segs.length - 1] : String(token || "").toLowerCase();
  return last.replace(/\.(exe|cmd|bat|com)$/, "");
}

// The program a name runs, where one interpreter answers to several names.
// `python3` and `python3.12` are the same interpreter, and the macOS framework
// build's `python3` re-executes itself as Python.app's `Python`, so the process
// the endpoint records never carries the name the agent typed.
export function programFamily(name) {
  return /^python\d*(\.\d+)*$/.test(name) ? "python" : name;
}

/**
 * One shell command as the set of things a ProcessStart could be compared to.
 *
 * The whole line comes first, because a harness runs it as `sh -c "<line>"` and
 * the OS then sees the entire line inside one command line — the strongest
 * agreement available. Each simple command in it follows, for the case where
 * the shell forked and only the program is visible: a script (a Codex
 * code-mode cell runs several lines) is read the way the shell splits it, and
 * each command from the program the OS starts, past the assignments and
 * reserved words the shell consumed first (shellScript.js).
 */
export function shellCommandCandidates(command) {
  const whole = normalizeSpace(command);
  if (!whole) return [];
  const out = [];
  if (whole.length >= MIN_VERBATIM_CHARS) out.push({ text: whole, argv: null });
  const seen = new Set();
  for (const part of scriptCommands(command)) {
    const proper = commandProper(part);
    if (!proper) continue;
    const argv = tokenize(proper);
    // Fewer than two tokens is the coincidence case: a bare `ls` or `pwd`
    // appearing in a window says nothing about this trace.
    if (argv.length < 2) continue;
    if (SHELL_BUILTINS.has(programName(argv[0]))) continue;
    const text = normalizeSpace(proper);
    if (seen.has(text)) continue;
    seen.add(text);
    out.push({ text, argv });
  }
  return out;
}

const SHELL_WRAPPER_FLAGS = new Map([
  ["sh", new Set(["-c", "-lc"])],
  ["bash", new Set(["-c", "-lc"])],
  ["zsh", new Set(["-c", "-lc"])],
  ["dash", new Set(["-c", "-lc"])],
  ["pwsh", new Set(["-c", "-command"])],
  ["powershell", new Set(["-c", "-command"])],
  ["cmd", new Set(["/c"])],
]);

// The script argument of a shell wrapper, or null. Quote removal belongs here,
// after the executable and execution flag have been verified; stripping a
// quote from an arbitrary command line turns quoted arguments into false
// verbatim matches (`git commit -m 'git add .'`).
export function shellWrapperScript(line) {
  const match = /^("[^"]*"|'[^']*'|\S+)\s+(\S+)\s+(.+)$/.exec(line);
  if (!match) return null;
  const wrappedProgram = match[1].replace(/^(?:"([^"]*)"|'([^']*)')$/, "$1$2");
  const flags = SHELL_WRAPPER_FLAGS.get(programName(wrappedProgram));
  const flag = match[2].toLowerCase();
  if (!flags?.has(flag)) return null;
  let script = match[3].trim();
  const quoted = script.length >= 2
    && ((script[0] === '"' && script.at(-1) === '"') || (script[0] === "'" && script.at(-1) === "'"));
  if (quoted) {
    script = script.slice(1, -1);
  }
  return normalizeSpace(script);
}

// A shell expansion in a candidate's arguments: `$name`, `${name}` or `$(...)`.
const EXPANSION_RE = /\$\{[A-Za-z_][A-Za-z0-9_]*\}|\$[A-Za-z_][A-Za-z0-9_]*|\$\([^)]*\)/;
const MAX_EXPANSIONS = 8;

// Do the observed arguments read as the candidate's with each expansion filled
// by some text? The literal pieces must appear in order, with at least a
// character for each expansion, and together carry as much text as a verbatim
// match needs. Taking each piece at its leftmost place decides this without
// backtracking.
function filledIn(observed, template) {
  const parts = template.split(new RegExp(EXPANSION_RE.source, "g"));
  if (parts.length < 2 || parts.length > MAX_EXPANSIONS + 1) return false;
  if (parts.join("").replace(/\s/g, "").length < MIN_VERBATIM_CHARS) return false;
  const first = parts[0];
  const last = parts[parts.length - 1];
  if (!observed.startsWith(first) || !observed.endsWith(last)) return false;
  const limit = observed.length - last.length;
  let pos = first.length;
  for (let i = 1; i < parts.length - 1; i += 1) {
    const at = observed.indexOf(parts[i], pos + 1);
    if (at < 0 || at + parts[i].length > limit - 1) return false;
    pos = at + parts[i].length;
  }
  return limit - pos >= 1;
}

/**
 * Does this observed command line correspond to a candidate the row ran?
 * `{ rank, basis }` or null; `filled` marks a match that let the script's
 * variables stand for whatever the process's arguments hold there.
 */
export function matchCommand(commandLine, candidate) {
  const line = normalizeSpace(commandLine);
  if (!line || !candidate) return null;
  if (candidate.text.length >= MIN_VERBATIM_CHARS && line === candidate.text) {
    return { rank: RANK_EXACT, basis: "the endpoint recorded this command verbatim" };
  }
  if (candidate.text.length >= MIN_VERBATIM_CHARS && shellWrapperScript(line) === candidate.text) {
    return { rank: RANK_EXACT, basis: "the endpoint's shell wrapper carries this command verbatim" };
  }
  if (!candidate.argv || candidate.argv.length < 2) return null;
  const argv = tokenize(line);
  if (argv.length < 2) return null;
  const observedProgram = programName(argv[0]);
  const candidateProgram = programName(candidate.argv[0]);
  const family = programFamily(observedProgram);
  if (family !== programFamily(candidateProgram)) return null;
  const program = observedProgram === candidateProgram ? "program" : `${family} interpreter`;
  const wrapperFlagsIgnoreCase = observedProgram === "powershell" || observedProgram === "pwsh" || observedProgram === "cmd";
  const sameArg = (arg, other) => (wrapperFlagsIgnoreCase && /^[-/]/.test(other)
    ? arg.toLowerCase() === other.toLowerCase()
    : arg === other);
  if (argv.length === candidate.argv.length && argv.slice(1).every((arg, i) => sameArg(arg, candidate.argv[i + 1]))) {
    return { rank: RANK_SUFFIX, basis: `the same ${program} and arguments` };
  }
  // The endpoint records a command line as its arguments joined by spaces, so
  // an argument holding one (`jq -r '.[] | .id'`) comes back as several
  // words: the same words in the same order are the same arguments.
  const observedArgs = normalizeSpace(argv.slice(1).join(" "));
  const candidateArgs = normalizeSpace(candidate.argv.slice(1).join(" "));
  if (observedArgs === candidateArgs) {
    return { rank: RANK_SUFFIX, basis: `the same ${program} and arguments` };
  }
  // A script's variables hold their values by the time its process starts.
  if (EXPANSION_RE.test(candidateArgs) && filledIn(observedArgs, candidateArgs)) {
    return { rank: RANK_SUFFIX, basis: `the same ${program} and arguments, with the script's variables filled in`, filled: true };
  }
  // Interpreter execution flags (`-c`, `-Command`, `/c`) identify only the
  // wrapper, not the script it ran. Treating one as an argument floor would
  // make every command from that interpreter look related.
  if (/^[-/]/.test(candidate.argv[1])) return null;
  if (argv[1] !== candidate.argv[1]) return null;
  return { rank: RANK_FLOOR, basis: `the same ${program} and first argument` };
}

// The command a residual tool call issued, read straight off the block.
//
// Not readToolCallHead: that is a DISPLAY projection — it prefixes `command: `
// when the call also carried a description, and it stops at the first newline —
// and matching a display string would silently miss every multi-line command
// and every call that named its own purpose.
function shellCommandOf(turns, item) {
  const block = turns?.[item?.turnIndex]?.blocks?.[item?.blockIndex];
  if (!block || block.type !== "tool_use") return null;
  return shellToolCommand(block.name, block.input);
}

/**
 * What a stretch of feed rows NAMES that an endpoint event could correspond to.
 *
 * @param rows   the feed rows covered by one turn window, in render order
 * @param turns  the trace's turns, for reading a residual call's command back
 * @returns { paths, commands } — plain strings, deduped, order preserved
 *
 * Scoped to the whole stretch rather than the one row that carries the
 * annotation: every row of a stretch shares the window, so a file edited two
 * rows down is inside the same span of time and is exactly as corroborable.
 */
export function windowCandidates(rows, turns) {
  const paths = new Set();
  const commands = new Set();
  for (const row of rows || []) {
    if (row?.type === "ops" && row.domain === "files") {
      for (const it of row.items || []) {
        if (typeof it?.op?.path === "string" && it.op.path.trim()) paths.add(it.op.path);
      }
    } else if (row?.type === "residual") {
      for (const it of row.items || []) {
        const cmd = shellCommandOf(turns, it);
        if (cmd) commands.add(cmd);
      }
    }
  }
  return { paths: [...paths], commands: [...commands] };
}

/** Is there anything here a match could be found against? */
export function hasCandidates(candidates) {
  return Boolean(candidates && (candidates.paths?.length || candidates.commands?.length));
}

// Which field of an event names a file, and how that reads. FileState is the
// content-state event, so its verb is the one a reader expects for a write.
function fileTargets(kind, data) {
  if (!data) return [];
  if (kind === "FileState") return [{ path: data.filepath, verb: "written" }];
  if (kind === "FileDelete") return [{ path: data.filepath, verb: "deleted" }];
  if (kind === "FileRename") {
    return [
      { path: data.new_filepath, verb: "renamed to" },
      { path: data.original_filepath, verb: "renamed from" },
    ];
  }
  return [];
}

function fold(out, m) {
  const cur = out.get(m.key);
  if (!cur) {
    out.set(m.key, { ...m, count: 1 });
    return;
  }
  cur.count += 1;
  if (Number.isFinite(m.ts) && (!Number.isFinite(cur.ts) || m.ts < cur.ts)) cur.ts = m.ts;
  // Keep the strongest reading of a correspondence found several ways: a
  // window that wrote both `/repo/src/a.js` and some other `src/a.js` should
  // report the exact path, not the weaker suffix agreement.
  if (m.rank > cur.rank) {
    cur.rank = m.rank;
    cur.basis = m.basis;
    cur.observed = m.observed;
  }
}

/**
 * The correspondences between one window's endpoint events and what its rows
 * named.
 *
 * @param events      [{ id, ts, kind, data }] — the DETAILED projection; an
 *                    event with no `data` cannot be matched and is skipped
 * @param candidates  from windowCandidates
 * @returns [{ key, kind, verb, observed, candidate, basis, rank, ts, count }]
 *          strongest first, then earliest.
 *
 * Folded per (kind, verb, candidate): a window where one file was rewritten
 * forty times is ONE correspondence observed forty times, not forty lines.
 */
export function matchWindowEvents(events, candidates) {
  const paths = (candidates?.paths || []).filter((p) => typeof p === "string" && p.trim());
  const commands = (candidates?.commands || []).flatMap(shellCommandCandidates);
  if (!paths.length && !commands.length) return [];
  const out = new Map();
  for (const e of events || []) {
    if (!e || !e.data) continue;
    if (e.kind === "ProcessStart") {
      const line = e.data.command_line || e.data.image_name || "";
      for (const cand of commands) {
        const hit = matchCommand(line, cand);
        if (!hit) continue;
        fold(out, {
          key: `cmd|${cand.text}`,
          kind: "ProcessStart",
          verb: "ran",
          observed: normalizeSpace(line),
          candidate: cand.text,
          basis: hit.basis,
          rank: hit.rank,
          ts: e.ts,
        });
      }
      continue;
    }
    for (const target of fileTargets(e.kind, e.data)) {
      if (!target.path) continue;
      for (const cand of paths) {
        const hit = matchPath(target.path, cand);
        if (!hit) continue;
        fold(out, {
          key: `file|${e.kind}|${target.verb}|${cand}`,
          kind: e.kind,
          verb: target.verb,
          observed: String(target.path),
          candidate: cand,
          basis: hit.basis,
          rank: hit.rank,
          ts: e.ts,
        });
      }
    }
  }
  return [...out.values()].sort(
    (a, b) => b.rank - a.rank || (a.ts ?? Infinity) - (b.ts ?? Infinity) || a.key.localeCompare(b.key),
  );
}
